import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center whitespace-nowrap transition-colors",
  {
    variants: {
      variant: {
        light: "",
        filled: "",
      },
      color: {
        primary: "",
        positive: "",
        negative: "",
        notice: "",
        info: "",
        neutral: "",
      },
      size: {
        small:
          "px-2 h-[var(--size-20)]  gap-2 rounded-large text-body-small-medium",
        medium:
          "px-3 h-[var(--size-24)]  gap-3 rounded-large text-body-medium-medium",
        large:
          "px-4 h-[var(--size-28)] gap-3 rounded-xlarge text-body-large-medium",
      },
    },
    compoundVariants: [
      // Light variant colors
      {
        variant: "light",
        color: "primary",
        class: "bg-action-fill-primary-faded text-action-ink-primary-normal",
      },
      {
        variant: "light",
        color: "positive",
        class: "bg-action-fill-positive-faded text-action-ink-positive-normal",
      },
      {
        variant: "light",
        color: "negative",
        class: "bg-action-fill-negative-faded text-action-ink-negative-normal",
      },
      {
        variant: "light",
        color: "notice",
        class: "bg-action-fill-notice-faded text-action-ink-notice-normal",
      },
      {
        variant: "light",
        color: "info",
        class: "bg-action-fill-info-faded text-action-ink-info-normal",
      },
      {
        variant: "light",
        color: "neutral",
        class: "bg-action-fill-neutral-faded text-action-ink-neutral-normal",
      },
      // Filled variant colors
      {
        variant: "filled",
        color: "primary",
        class:
          "bg-action-fill-primary-default text-action-ink-on-primary-normal",
      },
      {
        variant: "filled",
        color: "positive",
        class:
          "bg-action-fill-positive-default text-action-ink-on-primary-normal",
      },
      {
        variant: "filled",
        color: "negative",
        class:
          "bg-action-fill-negative-default text-action-ink-on-primary-normal",
      },
      {
        variant: "filled",
        color: "notice",
        class:
          "bg-action-fill-notice-default text-action-ink-on-primary-normal",
      },
      {
        variant: "filled",
        color: "info",
        class: "bg-action-fill-info-default text-action-ink-on-primary-normal",
      },
      {
        variant: "filled",
        color: "neutral",
        class:
          "bg-action-fill-neutral-default text-action-ink-on-primary-normal",
      },
    ],
    defaultVariants: {
      variant: "light",
      color: "info",
      size: "medium",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof badgeVariants> {
  /**
   * Whether to show a dot indicator on the left side
   */
  showDot?: boolean;
  /**
   * Custom class name for the badge
   */
  className?: string;
  /**
   * Badge label/content
   */
  children: React.ReactNode;
  /**
   * The color variant of the badge
   */
  color?: "primary" | "positive" | "negative" | "notice" | "info" | "neutral";
  /**
   * Leading icon to display before the badge content
   */
  leadingIcon?: React.ReactNode;
  /**
   * Trailing icon to display after the badge content
   */
  trailingIcon?: React.ReactNode;
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    { className, variant, size, color, showDot = false, leadingIcon, trailingIcon, children, ...props },
    ref
  ) => {
    const getDotColor = () => {
      if (variant === "filled") {
        return "bg-action-ink-on-primary-normal";
      }
      // Light variant - use the corresponding action color
      switch (color) {
        case "primary":
          return "bg-action-fill-primary-default";
        case "positive":
          return "bg-action-fill-positive-default";
        case "negative":
          return "bg-action-fill-negative-default";
        case "notice":
          return "bg-action-fill-notice-default";
        case "info":
          return "bg-action-fill-info-default";
        case "neutral":
          return "bg-action-fill-neutral-default";
        default:
          return "bg-action-fill-info-default";
      }
    };

    const getDotSize = () => {
      if (size === "small") {
        return "h-2 w-2";
      }
      if (size === "medium") {
        return "h-[6px] w-[6px]";
      }
      return "h-3 w-3";
    };

    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, size, color }), className)}
        {...props}
      >
        {showDot && (
          <span
            className={cn("rounded-full", getDotColor(), getDotSize())}
            aria-hidden="true"
          />
        )}
        {leadingIcon && <span className="inline-flex">{leadingIcon}</span>}
        {children}
        {trailingIcon && <span className="inline-flex">{trailingIcon}</span>}
      </div>
    );
  }
);

Badge.displayName = "Badge";

export { Badge, badgeVariants };
